import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { User } from '../../shared/types/security/user';
import { SecurityService } from '../../shared/services/security/security.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { PersonRestService } from '../../shared/services/person/person-rest.service';
import * as _ from 'lodash';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SecurityRestService } from '../../shared/services/security/security-rest.service';

@Component({
  selector: 'wsd-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.css']
})
export class MainMenuComponent implements OnInit, OnDestroy, AfterViewInit {

  menu: MenuItem[];

  hostUrl: string = environment.hostUrl;

  user: User;

  private langChange: Subscription;

  private hasUserRole = false;

  private logoutUrl: string;

  constructor(private translate: TranslateService,
    private router: Router,
    private httpClient: HttpClient,
    private securityService: SecurityService,
    private securityRestService: SecurityRestService,
    private personService: PersonRestService) {
  }

  ngOnInit() {
    this.langChange = this.translate.onDefaultLangChange.subscribe(
      () => {
        this.securityService.getLoggedUser().subscribe(
          value => {
            this.user = value;
            if (this.user.id) {
              this.personService.getPersonFromAllPersons(this.user.consultantId).subscribe(person => {
                if (person.roles) {
                  this.hasUserRole = person.roles.length > 0;
                }
              },
                () => {
                },
                () => {
                  this.init();
                });
            } else {
              this.init();
            }
          },
          () => {
          }
        );
      });

    this.securityRestService.getLogoutUrl().subscribe(
      (url: string) => {
        this.logoutUrl = url;
      }
    );
  }

  private init() {
    const self = this;

    this.menu = [
      {
        label: self.translate.instant('MAIN_HEADER.HEADER_LINK.LOGIN'),
        url: self.hostUrl + '/login',
        visible: false
      },
      {
        label: self.translate.instant('DATA_PORTAL.TITLE'),
        routerLink: ['data'],
        routerLinkActiveOptions: { exact: true },
        visible: Boolean(self.user.id),
        command: (event) => this.closeMobileMenu()
      },
      {
        label: self.translate.instant('WS_DIRECTORY.DIRECTORY'),
        routerLink: ['contacts'],
        routerLinkActiveOptions: { exact: true },
        visible: Boolean(self.user.id),
        command: (event) => this.closeMobileMenu()
      },
      {
        label: self.translate.instant('WS_DIRECTORY.ADMINISTRATION'),
        visible: Boolean(self.user.id),
        items: [
          {
            routerLink: ['persons/create'],
            routerLinkActiveOptions: { exact: true },
            label: self.translate.instant('WS_DIRECTORY.CREATE_PERSON'),
            command: (event) => this.closeMobileMenu()
          },
          {
            routerLink: ['organizations/create'],
            routerLinkActiveOptions: { exact: true },
            label: self.translate.instant('WS_DIRECTORY.CREATE_ORGANIZATION'),
            command: (event) => this.closeMobileMenu()
          },
          {
            routerLink: ['administration'],
            routerLinkActiveOptions: { exact: true },
            label: self.translate.instant('WS_DIRECTORY.CLASSIFICATIONS'),
            command: (event) => this.closeMobileMenu()
          },
          {
            routerLink: ['user-roles'],
            routerLinkActiveOptions: { exact: true },
            label: self.translate.instant('USER_ROLES.LINK_NAME'),
            command: (event) => this.closeMobileMenu()
          },
          {
            routerLink: ['notifications'],
            routerLinkActiveOptions: { exact: true },
            label: self.translate.instant('NOTIFICATIONS.LINK_NAME'),
            command: (event) => this.closeMobileMenu()
          },
          {
            routerLink: ['data/data-portal-administration'],
            routerLinkActiveOptions: { exact: true },
            label: self.translate.instant('DATA_PORTAL.LINK_NAME'),
            command: (event) => this.closeMobileMenu()
          },
          {
            routerLink: ['track-changes'],
            routerLinkActiveOptions: { exact: true },
            label: self.translate.instant('TRACK_CHANGES.LINK_NAME'),
            command: (event) => this.closeMobileMenu()
          },
          {
            routerLink: ['reports'],
            routerLinkActiveOptions: { exact: true },
            label: self.translate.instant('USER_REPORTS.LINK_NAME'),
            command: (event) => this.closeMobileMenu()
          },
        ]
      },
      {
        label: self.user.lastName,
        visible: false,
        items: [
          {
            label: self.translate.instant('MAIN_HEADER.PROFILE_MENU.PROFILE'),
            visible: self.user.directory,
            routerLink: ['persons', self.user && self.user.consultantId || 1, 'detail'],
            routerLinkActiveOptions: { exact: true },
            command: (event) => this.closeMobileMenu()
          },
          {
            label: self.translate.instant('MAIN_HEADER.PROFILE_MENU.LOGOUT'),
            command: () => self.logout()
          },
        ]
      }
    ];

    // VISIBILITY BY PERMISSIONS
    let item: MenuItem;
    this.securityService.getPermissions().subscribe(perms => {
      item = this.menu[3].items[0] as MenuItem;
      item.visible = perms.includes('WSD_PERSON_CREATE');

      item = this.menu[3].items[1] as MenuItem;
      item.visible = perms.includes('WSD_ORGANIZATION_CREATE');

      item = this.menu[3].items[2] as MenuItem;
      item.visible = perms.includes('WSD_CLASSIFICATIONS_READ');

      item = this.menu[3].items[3] as MenuItem;
      item.visible = perms.includes('WSD_USER_ROLES_READ');

      item = this.menu[3].items[4] as MenuItem;
      item.visible = perms.includes('WSD_NOTIFICATIONS_READ');

      item = this.menu[3].items[6] as MenuItem;
      item.visible = perms.includes('WSD_TRACK_CHANGES_READ');

      item = this.menu[3].items[7] as MenuItem;
      item.visible = perms.includes('WSD_IDM_USER_REPORT_READ');
    });

    // HIDE MENU ADMINISTRATION WHEN USER DOES NOT HAVE ANY PERMISSIONS
    const items = this.menu[3].items as MenuItem[];
    this.menu[3].visible = items.some((it: MenuItem) => it.visible === true);

    item = this.menu[4].items[0] as MenuItem;
    item.visible = this.user.hasRole;

    if (!this.hasUserRole) {
      this.menu[1].visible = false;
    }

    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        setTimeout(() => {
          this.updateParentLinks();
        });
      });
  }

  private logout() {
    const redirectUri = this.hostUrl !== '' ? this.hostUrl : 'http://localhost:4201/';
    this.httpClient.get(this.hostUrl + '/logout').subscribe(
      () => {
        window.location.href
          = this.logoutUrl + '?redirect_uri=' + redirectUri;
      },
      () => {
        window.location.href
          = this.logoutUrl + '?redirect_uri=' + redirectUri;
      }
    );
  }

  ngOnDestroy() {
    this.langChange.unsubscribe();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.updateParentLinks();
    }, 1000);
  }

  closeMobileMenu() {
    const elm: HTMLElement = window.document.querySelector('.burger-menu');
    if (elm) {
      elm.click();
    }
  }

  private updateParentLinks() {
    const subMenus = window.document.querySelectorAll('.p-submenu ul');
    subMenus.forEach(menu => {
      const menuLinkClassList = _.get(menu.parentNode, 'previousSibling.previousSibling.previousSibling.classList');
      if (!menuLinkClassList) {
        return;
      }
      if (menu.querySelectorAll('li a.p-state-active').length) {
        // @ts-ignore
        menuLinkClassList.add('p-state-active');
      } else {
        // @ts-ignore
        menuLinkClassList.remove('p-state-active');
      }
    });
  }

}
